import {
    AlgoliaAuctionSearchResults,
    AlgoliaMapBounds,
    AlgoliaSearchState,
    AuctionNearMeHit,
} from '@/pages/AuctionsNearMe/types/AuctionsNearMe.types';
import { Configure, InstantSearch } from 'react-instantsearch-dom';
import { Control, GeoSearch, GoogleMapsLoader, Marker } from 'react-instantsearch-dom-maps';
import {
    defaultAuctionsNearMeUkBounds,
    defaultAuctionsNearMeUsaBounds,
} from '@/pages/AuctionsNearMe/constants/AuctionsNearMeConstants';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { GeoArea } from '@/types/GeoArea';
import { getAlgoliaApiKey, getAlgoliaAppId, getGoogleApiKey } from '@/redux/modules/config';
import { getAlgoliaMapFilters } from '@/pages/AuctionsNearMe/helpers/getAlgoliaMapFilters';
import { getInfoWindowContent } from '@/pages/AuctionsNearMe/helpers/getInfoWindowContent';
import { getLocale } from '@/redux/modules/intl';
import { getSsrAuctionsNearMeHitsSelector } from '@/redux/modules/auctionsNearMe/auctionsNearMe.selectors';
import { useAppSelector } from '@/redux/hooks';
import algoliasearch from 'algoliasearch';
import AuctionsNearMeCardsView from '@/pages/AuctionsNearMe/components/MapView/components/CardsView/AuctionsNearMeCardsView';
import AuctionsNearMeSearchInput from '@/pages/AuctionsNearMe/components/MapView/components/SearchInput/AuctionsNearMeSearchInput';
import React, { ReactElement, useEffect, useState } from 'react';
import styled from 'styled-components';
import Text from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import theme from '@liveauctioneers/caterwaul-components/lib/theme/theme';
import useIsWiderThan from '@/hooks/useIsWiderThan';

export type AuctionsNearMeMapViewProps = {
    geoArea: GeoArea;
    geocoderFailed: boolean;
    setGeocoderFailed: (failed: boolean) => void;
    setHasFilteredResults: (hasResults: boolean) => void;
    setPageTitleSearchTerm: (term: string) => void;
};

const AuctionsNearMeMapView = ({
    geoArea,
    geocoderFailed,
    setGeocoderFailed,
    setHasFilteredResults,
    setPageTitleSearchTerm,
}: AuctionsNearMeMapViewProps): ReactElement => {
    const intl = useIntl();
    const locale = useAppSelector(getLocale);
    const indexName = 'Available_Catalogs';
    const googleMapsApiKey = useAppSelector(getGoogleApiKey);
    const algoliaApiKey = useAppSelector(getAlgoliaApiKey);
    const algoliaAppId = useAppSelector(getAlgoliaAppId);
    // Get SSR hits from Redux for initial render
    const ssrHits = useAppSelector(getSsrAuctionsNearMeHitsSelector);
    const [hits, setHits] = useState<AuctionNearMeHit[]>(ssrHits);
    const [searchClient] = useState(algoliasearch(algoliaAppId, algoliaApiKey));
    // NOTE: the google/infoWindow typings are hidden in third-party namespaces that cannot be imported without causing compilation errors, thus the 'any' type
    const [google, setGoogle] = useState<any>();
    const [infoWindow, setInfoWindow] = useState<any>();
    const defaultBounds = intl.locale === 'en-gb' ? defaultAuctionsNearMeUkBounds : defaultAuctionsNearMeUsaBounds;
    const [searchState, setSearchState] = useState<AlgoliaSearchState>({
        boundingBox: defaultBounds,
    });
    // Initialize loading state based on whether we have SSR hits - same on server and client
    const [isLoading, setIsLoading] = useState(ssrHits.length === 0);
    const [filteredHits, setFilteredHits] = useState<AuctionNearMeHit[]>([]);
    const isMobile = !useIsWiderThan(theme.breakpoints.tabletNarrowWidthNum);

    useEffect(() => {
        if (google && !infoWindow) {
            setInfoWindow(new google.maps.InfoWindow());
        }
    }, [google, infoWindow]);

    useEffect(() => {
        if (!geoArea || !google) {
            return;
        }
        const geoAreaBounds: AlgoliaMapBounds = {
            northEast: {
                lat: geoArea.northEastLatitude,
                lng: geoArea.northEastLongitude,
            },
            southWest: {
                lat: geoArea.southWestLatitude,
                lng: geoArea.southWestLongitude,
            },
        };
        setSearchState((previousSearchState) => ({
            ...previousSearchState,
            boundingBox: geoAreaBounds,
        }));
    }, [geoArea, google]);

    useEffect(() => {
        if (filteredHits.length > 0) {
            setIsLoading(false);
        }
    }, [filteredHits]);

    const onSearchStateChange = (searchState): void => {
        setSearchState((previousSearchState) => {
            return {
                ...previousSearchState,
                boundingBox: searchState.boundingBox,
            };
        });
    };

    const toggleAuctionHouseInfo = (hit: AuctionNearMeHit, marker): void => {
        if (!google) {
            return;
        }
        infoWindow.close();
        infoWindow.setContent(getInfoWindowContent(hit, filteredHits));
        infoWindow.open(marker.getMap(), marker);
    };

    const numberOfAuctionHouses = filteredHits.filter(
        (hit, index) =>
            filteredHits.findIndex(
                ({ _geoloc }) =>
                    _geoloc && hit._geoloc && _geoloc.lat === hit._geoloc.lat && _geoloc.lng === hit._geoloc.lng
            ) === index
    ).length;

    return (
        <InstantSearch
            indexName={indexName}
            onSearchStateChange={onSearchStateChange}
            searchClient={searchClient}
            searchState={searchState}
        >
            <Configure
                filters={getAlgoliaMapFilters()}
                hitsPerPage={1000}
            />
            <StyledResults>
                <StyledCardsColumn>
                    <AuctionsNearMeCardsView
                        geoArea={geoArea}
                        geocoderFailed={geocoderFailed}
                        hits={hits}
                        setHasFilteredResults={setHasFilteredResults}
                        updateFilteredHits={setFilteredHits}
                    />
                </StyledCardsColumn>
                <StyledMap data-testid="auctionsHouseSection">
                    {!isLoading
                        ? React.cloneElement(
                              <StyledAuctionHouseCount h3>
                                  {numberOfAuctionHouses === 1 ? (
                                      <FormattedMessage id="nearby_auction_houses_count_single" />
                                  ) : (
                                      <FormattedMessage
                                          id="nearby_auction_houses_count"
                                          values={{
                                              numberOfHouses: Intl.NumberFormat(locale).format(numberOfAuctionHouses),
                                          }}
                                      />
                                  )}
                              </StyledAuctionHouseCount>,
                              {
                                  h2: isMobile,
                                  h3: !isMobile,
                              }
                          )
                        : null}
                    <AuctionsNearMeSearchInput
                        currentGeoArea={geoArea}
                        onSearchResults={(newBounds) => {
                            setSearchState((previousSearchState) => ({
                                ...previousSearchState,
                                boundingBox: newBounds,
                            }));
                        }}
                        setGeocoderFailed={setGeocoderFailed}
                        setPageTitleSearchTerm={setPageTitleSearchTerm}
                    />
                    <GoogleMapsLoader apiKey={googleMapsApiKey}>
                        {(googleGlobal) => {
                            setGoogle(googleGlobal);
                            return (
                                <GeoSearch google={googleGlobal}>
                                    {({ hits: algoliaHits }: AlgoliaAuctionSearchResults) => {
                                        setHits(algoliaHits);
                                        return (
                                            <div>
                                                <Control />
                                                {filteredHits.map((hit) => (
                                                    <Marker
                                                        anchor={{ x: 0, y: 5 }}
                                                        hit={hit}
                                                        key={hit.objectID}
                                                        onClick={({ marker }) => {
                                                            toggleAuctionHouseInfo(hit, marker);
                                                        }}
                                                    />
                                                ))}
                                            </div>
                                        );
                                    }}
                                </GeoSearch>
                            );
                        }}
                    </GoogleMapsLoader>
                </StyledMap>
            </StyledResults>
        </InstantSearch>
    );
};

export default AuctionsNearMeMapView;

const StyledResults = styled.section`
    display: flex;
    margin-bottom: 16px;
    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        flex-direction: column-reverse;
    }
`;

const StyledCardsColumn = styled.div`
    [class^='paginator'] {
        justify-content: flex-end;
    }

    flex: 1 1 calc(100% - 590px);
    @media (min-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidthNum + 1}px) {
        margin-right: 20px;
    }
`;

const StyledMap = styled.section`
    .ais-GeoSearch {
        @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
            height: 33vh;
        }
        @media (min-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidthNum + 1}px) {
            height: 500px;
        }
    }
    .ais-GeoSearch-map {
        height: 100%;
    }
    .ais-GeoSearch-input {
        margin-right: 5px;
    }

    flex: 1 1 calc(100% - 590px);

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        margin-bottom: 10px;
    }
    @media (min-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidthNum + 1}px) {
        height: fit-content;
        position: sticky;
        top: 32px;
        margin-top: 32px;
    }
`;

const StyledAuctionHouseCount = styled(Text)`
    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        margin-top: 32px;
        margin-bottom: 8px;
    }
    @media (min-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidthNum + 1}px) {
        margin-bottom: 16px;
    }
`;
